/**
 * The Documents subtab: library list + editor.
 *
 * Lists the patient's editable documents ({@link DocumentLibrary}), creates new
 * markdown documents, and opens a selected document in the
 * {@link DocumentEditor} (per-section WYSIWYG, autosave drafts, commit + diff +
 * schema guard). Clinical content lives in the editor's hook state; nothing is
 * logged (HIPAA).
 *
 * The library list starts collapsed to a narrow rail (BLA-313): it typically
 * holds a single derivative document, so giving it a full-width panel by
 * default left the editor — the primary object on this surface — the
 * narrowest column on screen. The rail shows one abbreviated, selectable
 * button per document (same accessible name as the full row) and a toggle
 * to expand back to the full list for create/rename/delete.
 *
 * When expanded, the list header hosts a toolbar with title search filtering,
 * sort order controls (date or alphabetical, ascending or descending), a
 * refresh button that reloads the library and profile status while re-running
 * profile auto-import, and an add-document toggle.
 *
 * **Auto-selects the patient's generated profile on arrival (ADR-073, BLA-314):**
 * once the library and {@link useProfiles} have both loaded, if nothing has
 * been selected/opened yet, the profile document (if one exists) opens
 * automatically — no extra click. This runs once per mount; it never fights a
 * manual selection, a citation reveal, or a subsequent deliberate deselection.
 * Likewise, when background profile auto-import completes, the panel refreshes
 * the library and profiles, auto-selecting the imported profile if nothing is
 * currently selected.
 * A patient with no profile keeps the existing honest "Select or create a
 * document to edit it." empty state — never a blank pane.
 */
import React, { useEffect, useState } from "react";
import { useAutoImportProfile } from "../../hooks/useAutoImportProfile";
import { useDocumentLibrary } from "../../hooks/useDocumentLibrary";
import { useProfiles } from "../../hooks/useProfiles";
import DocumentEditor from "./DocumentEditor";
import DocumentLibrary, { LIBRARY_TYPES } from "./DocumentLibrary";
import type { BlueprintDocument } from "../../types";

/**
 * A two-letter abbreviation for a document's rail button.
 *
 * Document titles are always non-empty (create/rename both reject a blank
 * title), so no empty-title fallback is needed here.
 */
function railAbbreviation(title: string): string {
  return title.trim().slice(0, 2).toUpperCase();
}

/** Props for {@link DocumentRail}. */
interface DocumentRailProps {
  /** All of the patient's documents. */
  documents: BlueprintDocument[];
  /** The currently selected document id, if any. */
  selectedId: string | null;
  /** Select a document for editing. */
  onSelect: (documentId: string) => void;
}

/** The collapsed narrow-rail view of the document library. */
function DocumentRail({
  documents,
  selectedId,
  onSelect,
}: DocumentRailProps): React.ReactElement {
  const library = documents.filter((d) => LIBRARY_TYPES.has(d.type));
  return (
    <ul className="space-y-1" data-testid="document-rail">
      {library.map((doc) => (
        <li key={doc.id}>
          <button
            type="button"
            title={doc.title}
            aria-label={doc.title}
            aria-pressed={doc.id === selectedId}
            onClick={() => onSelect(doc.id)}
            className={`flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-semibold ${
              doc.id === selectedId
                ? "bg-blue-50 border border-blue-300 text-blue-700"
                : "border border-transparent text-slate-500 hover:bg-slate-50"
            }`}
          >
            {railAbbreviation(doc.title)}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Props for {@link DocumentsPanel}. */
interface DocumentsPanelProps {
  /** The active patient id. */
  patientId: string;
  /**
   * A document id to open in the editor (a "Go to source" on a `derivative:`
   * citation — ADR-011). Paired with {@link openKey} so a repeat reveal of the
   * same document re-opens it.
   */
  openDocumentId?: string | null;
  /** Monotonic key — a change re-asserts opening {@link openDocumentId}. */
  openKey?: number;
}

/**
 * Render the Documents subtab for a patient.
 *
 * @param props - The active patient id + an optional document to open.
 * @returns The documents-panel element.
 */
export const DocumentsPanel: React.FC<DocumentsPanelProps> = ({
  patientId,
  openDocumentId = null,
  openKey,
}) => {
  const library = useDocumentLibrary(patientId);
  const profiles = useProfiles(patientId);
  const autoImport = useAutoImportProfile(patientId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The library list starts collapsed to a narrow rail (BLA-313) — expand to
  // reach create/rename/delete.
  const [listOpen, setListOpen] = useState(false);
  // Runs once per mount to decide the initial rail state from the FIRST
  // load of this patient's library. Relies on the parent (`DocumentsTab`)
  // mounting this component with `key={patientId}` so a patient switch is a
  // fresh mount — `useDocumentLibrary`'s `status`/`documents` only reset on
  // its own internal `useEffect` (after render), so without a remount here,
  // the first render after a patient switch would still read the PREVIOUS
  // patient's already-`"loaded"` data and could cache the wrong decision.
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);

  // Search and sort toolbar state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<"date" | "alphabetical">("date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [isAddOpen, setIsAddOpen] = useState(false);

  const toggleSortField = (): void => {
    setSortField((prev) => (prev === "date" ? "alphabetical" : "date"));
  };

  const toggleSortDirection = (): void => {
    setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  const { reload: reloadLibrary } = library;
  const { reload: reloadProfiles } = profiles;

  const handleRefresh = (): void => {
    reloadLibrary();
    autoImport.refresh();
    reloadProfiles();
  };

  if (library.status === "loaded" && !hasAutoExpanded) {
    setHasAutoExpanded(true);
    if (
      library.documents.filter((d) => LIBRARY_TYPES.has(d.type)).length === 0
    ) {
      setListOpen(true);
    }
  }

  // The id last opened via a citation reveal — used only to show a "(source
  // removed)" notice when that specific derivative was deleted (ADR-011);
  // cleared on any manual selection/create so it never mislabels those.
  const [revealedId, setRevealedId] = useState<string | null>(null);
  // Guards the profile auto-select to a single attempt per mount (ADR-073) so
  // it never re-fires after a deliberate later deselection (e.g. a delete).
  const [autoSelectAttempted, setAutoSelectAttempted] = useState(false);

  // Open a revealed document (a derivative "Go to source"): guarded setState on
  // a prop change (NOT an effect), tracking the last-seen key so a repeat reveal
  // re-opens while a manual selection in between still sticks.
  const [seenOpenKey, setSeenOpenKey] = useState<number | undefined>(undefined);
  let openedByRevealThisRender = false;
  if (
    openDocumentId !== null &&
    openKey !== undefined &&
    openKey !== seenOpenKey
  ) {
    setSeenOpenKey(openKey);
    setSelectedId(openDocumentId);
    setRevealedId(openDocumentId);
    openedByRevealThisRender = true;
  }

  // Auto-select the generated profile on arrival (ADR-073): only when nothing
  // else has claimed selection this render, nothing was selected already, and
  // both the library and profile list have settled.
  if (
    !openedByRevealThisRender &&
    !autoSelectAttempted &&
    library.status === "loaded" &&
    (profiles.status === "loaded" || profiles.status === "error")
  ) {
    setAutoSelectAttempted(true);
    if (selectedId === null && profiles.profiles.length > 0) {
      setSelectedId(profiles.profiles[0].document.id);
    }
  }

  const selectManually = (id: string | null): void => {
    setRevealedId(null);
    setSelectedId(id);
  };

  // A revealed derivative that is absent from the loaded library was deleted:
  // degrade gracefully to "(source removed)" instead of a broken editor.
  const revealRemoved =
    selectedId !== null &&
    selectedId === revealedId &&
    library.status === "loaded" &&
    !library.documents.some((doc) => doc.id === selectedId);

  const handleCreate = (title: string): void => {
    void library.create(title, "markdown").then((doc) => {
      if (doc) selectManually(doc.id);
    });
  };

  const handleRename = (documentId: string, title: string): void => {
    void library.rename(documentId, title);
  };

  const handleDelete = (documentId: string): void => {
    void library.remove(documentId).then((ok) => {
      // Clear the editor if the deleted document was the open one.
      if (ok && documentId === selectedId) setSelectedId(null);
    });
  };

  const importedDocumentId = autoImport.result?.imported
    ? autoImport.result.document_id
    : null;

  useEffect(() => {
    if (importedDocumentId !== null) {
      reloadLibrary();
      reloadProfiles();
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setSelectedId((prev) => (prev === null ? importedDocumentId : prev));
    }
  }, [importedDocumentId, reloadLibrary, reloadProfiles]);

  const isRefreshing =
    autoImport.importing ||
    library.status === "loading" ||
    profiles.status === "loading";

  return (
    <div className="flex h-full overflow-hidden">
      <div
        className={`shrink-0 border-r border-slate-200 overflow-y-auto ${
          listOpen ? "w-80 p-4" : "w-12 p-2"
        }`}
      >
        <div
          className={`flex items-center mb-2 ${
            listOpen ? "justify-between" : "justify-center"
          }`}
        >
          {listOpen && (
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Documents
            </span>
          )}
          <button
            type="button"
            data-testid="toggle-document-list"
            aria-label={
              listOpen ? "Collapse document list" : "Expand document list"
            }
            onClick={() => setListOpen((open) => !open)}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            {listOpen ? "«" : "»"}
          </button>
        </div>
        {listOpen ? (
          <>
            <div className="flex items-center gap-1.5 mb-2">
              <div className="relative flex-1 min-w-0">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search documents…"
                  aria-label="Search documents"
                  data-testid="documents-search-input"
                  className="w-full pl-7 pr-6 py-1 text-xs rounded-md border border-slate-300 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                />
                <svg
                  className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 1114 0z"
                  />
                </svg>
                {searchQuery !== "" && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    data-testid="documents-search-clear"
                    className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600 text-xs font-bold leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={toggleSortField}
                data-testid="documents-sort-field-button"
                title={
                  sortField === "date" ? "Sort alphabetically" : "Sort by date"
                }
                aria-label={
                  sortField === "date" ? "Sort alphabetically" : "Sort by date"
                }
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0"
              >
                {sortField === "date" ? (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                ) : (
                  <span className="font-bold text-[11px] leading-none">
                    A-Z
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={toggleSortDirection}
                data-testid="documents-sort-direction-button"
                title={
                  sortDirection === "desc"
                    ? "Sort ascending"
                    : "Sort descending"
                }
                aria-label={
                  sortDirection === "desc"
                    ? "Sort ascending"
                    : "Sort descending"
                }
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0"
              >
                {sortDirection === "desc" ? (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <polyline points="19 12 12 19 5 12" />
                  </svg>
                ) : (
                  <svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="19" x2="12" y2="5" />
                    <polyline points="5 12 12 5 19 12" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                aria-label="Refresh documents"
                title="Refresh documents"
                data-testid="refresh-documents-button"
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg
                  className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-blue-600" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => setIsAddOpen((prev) => !prev)}
                aria-label="New document"
                title="New document"
                data-testid="add-document-header-button"
                className={`w-7 h-7 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors text-xs shrink-0 ${
                  isAddOpen ? "bg-slate-100 text-blue-600 border-blue-300" : ""
                }`}
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            </div>
            {library.status === "error" ? (
              <p className="text-sm text-rose-600">Unable to load documents.</p>
            ) : (
              <DocumentLibrary
                documents={library.documents}
                selectedId={selectedId}
                onSelect={selectManually}
                onCreate={handleCreate}
                onRename={handleRename}
                onDelete={handleDelete}
                searchQuery={searchQuery}
                onClearSearch={() => setSearchQuery("")}
                sortField={sortField}
                sortDirection={sortDirection}
                isCreateOpen={isAddOpen}
              />
            )}
          </>
        ) : library.status === "error" ? (
          <p className="text-sm text-rose-600">Unable to load documents.</p>
        ) : (
          <DocumentRail
            documents={library.documents}
            selectedId={selectedId}
            onSelect={selectManually}
          />
        )}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        {revealRemoved ? (
          <p
            className="p-6 text-sm text-slate-500 italic"
            data-testid="source-removed"
          >
            (source removed) — the cited derivative document no longer exists.
          </p>
        ) : selectedId === null ? (
          <p className="p-6 text-sm text-slate-400 italic">
            Select or create a document to edit it.
          </p>
        ) : (
          <DocumentEditor documentId={selectedId} />
        )}
      </div>
    </div>
  );
};

export default DocumentsPanel;
